import React, { useState } from 'react';
import { SectionWrapper } from '../ui/SectionWrapper';
import { Button } from '../ui/Button';
import { Calendar, MapPin, Video, ChevronLeft, ChevronRight, X } from 'lucide-react';
import { useStore } from '../store/usestore';

export const Events = () => {
  const { events } = useStore();
  const [selectedEvent, setSelectedEvent] = useState<any>(null);
  const [showAll, setShowAll] = useState(false);
  const displayedEvents = showAll ? events : events.slice(0, 3);

  return (
    <SectionWrapper id="events" className="bg-gray-50">
      <div className="text-center mb-12">
        <h2 className="text-3xl md:text-4xl font-bold text-primary mb-4">Events & Mobilization</h2>
        <p className="text-gray-600 max-w-2xl mx-auto">
          Stay updated with our latest activities, rallies, and community engagement programs.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
        {displayedEvents.map((event) => (
          <EventCard key={event.id} event={event} onClick={() => setSelectedEvent(event)} />
        ))}
      </div>

      {events.length > 3 && (
        <div className="text-center">
          <Button 
            variant="outline" 
            className="border-primary text-primary hover:bg-primary hover:text-white"
            onClick={() => setShowAll(!showAll)}
          >
            {showAll ? 'Show Less' : 'View All Events'}
          </Button>
        </div>
      )}

      {/* Event Details Modal */}
      {selectedEvent && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm" onClick={() => setSelectedEvent(null)}>
          <div className="bg-white rounded-2xl w-full max-w-4xl max-h-[90vh] overflow-y-auto relative" onClick={e => e.stopPropagation()}>
            <button 
              onClick={() => setSelectedEvent(null)}
              className="absolute top-4 right-4 p-2 bg-black/10 hover:bg-black/20 rounded-full transition-colors z-10"
            >
              <X className="w-6 h-6" />
            </button>
            
            <div className="p-6 sm:p-8">
              <div className="mb-6">
                <div className="flex items-center text-sm text-primary font-medium mb-2 space-x-4">
                  <span className="flex items-center"><Calendar className="w-4 h-4 mr-1" /> {selectedEvent.date}</span>
                  <span className="flex items-center"><MapPin className="w-4 h-4 mr-1" /> {selectedEvent.location}</span>
                </div>
                <h3 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4">{selectedEvent.title}</h3>
                {selectedEvent.description && (
                   <p className="text-gray-600 mb-6">{selectedEvent.description}</p>
                )}
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {(selectedEvent.images && selectedEvent.images.length > 0 ? selectedEvent.images : (selectedEvent.image ? [selectedEvent.image] : [])).map((media: string, index: number) => {
                  const isVideo = media.match(/\.(mp4|webm|ogg|mov)$/i) || media.includes('videos');
                  return (
                    <div key={index} className="rounded-xl overflow-hidden bg-gray-100 shadow-sm aspect-video relative group">
                      {isVideo ? (
                        <video 
                          src={media} 
                          className="w-full h-full object-cover" 
                          controls 
                          preload="metadata"
                        />
                      ) : (
                        <img 
                          src={media} 
                          alt={`${selectedEvent.title} - ${index + 1}`} 
                          className="w-full h-full object-cover"
                          loading="lazy"
                        />
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      )}
    </SectionWrapper>
  );
};

const EventCard = ({ event, onClick }: { event: any; onClick: () => void }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [currentMediaIndex, setCurrentMediaIndex] = useState(0);
  
  const media = event.images && event.images.length > 0 ? event.images : (event.image ? [event.image] : []);
  const currentMedia = media[currentMediaIndex];
  const isVideo = currentMedia?.match(/\.(mp4|webm|ogg|mov)$/i) || currentMedia?.includes('videos');

  const nextMedia = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setCurrentMediaIndex((prev) => (prev + 1) % media.length);
  };

  const prevMedia = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setCurrentMediaIndex((prev) => (prev - 1 + media.length) % media.length);
  };

  return (
    <div className="bg-white rounded-xl overflow-hidden shadow-md hover:shadow-xl transition-shadow flex flex-col h-full cursor-pointer" onClick={onClick}>
      <div className="h-48 overflow-hidden relative">
        {isVideo && (
          <div className="absolute top-2 right-2 bg-black/50 p-1 rounded-md z-20">
            <Video className="w-4 h-4 text-white" />
          </div>
        )}
        {media.length > 1 && (
          <>
            <button onClick={prevMedia} className="absolute left-2 top-1/2 -translate-y-1/2 bg-black/50 p-1 rounded-full z-20 text-white hover:bg-black/70">
              <ChevronLeft className="w-4 h-4" />
            </button>
            <button onClick={nextMedia} className="absolute right-2 top-1/2 -translate-y-1/2 bg-black/50 p-1 rounded-full z-20 text-white hover:bg-black/70">
              <ChevronRight className="w-4 h-4" />
            </button>
            <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-1 z-20">
              {media.map((_: any, idx: number) => (
                <div key={idx} className={`w-1.5 h-1.5 rounded-full ${idx === currentMediaIndex ? 'bg-white' : 'bg-white/50'}`} />
              ))}
            </div>
          </>
        )}
        {isVideo ? (
          <>
            {isLoading && (
              <div className="absolute inset-0 flex items-center justify-center bg-gray-100 z-10">
                <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
              </div>
            )}
            <video 
              key={currentMedia}
              src={`${currentMedia}#t=0.1`}
              className={`w-full h-full object-cover transition-opacity duration-300 ${isLoading ? 'opacity-0' : 'opacity-100'}`}
              muted
              playsInline
              autoPlay
              loop
              preload="metadata"
              onLoadedData={() => setIsLoading(false)}
            />
          </>
        ) : (
          <img 
            src={currentMedia} 
            alt={event.title} 
            className="w-full h-full object-cover hover:scale-105 transition-transform duration-500" 
            loading="lazy"
            decoding="async"
          />
        )}
      </div>
      <div className="p-6 flex-1 flex flex-col">
        <div className="flex items-center text-sm text-primary font-medium mb-3 space-x-4">
          <span className="flex items-center"><Calendar className="w-4 h-4 mr-1" /> {event.date}</span>
        </div>
        <h3 className="text-xl font-bold text-gray-900 mb-2">{event.title}</h3>
        <div className="flex items-center text-sm text-gray-500 mb-4">
          <MapPin className="w-4 h-4 mr-1" /> {event.location}
        </div>
        <button className="text-accent font-semibold hover:underline mt-auto inline-block text-left">Read More &rarr;</button>
      </div>
    </div>
  );
};
